import React, { useState, useEffect, useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    Typography,
    Button,
    IconButton,
    SvgIcon,
    Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import NumberInput from '../NumberInput';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { DEFAULT_TIME_LIMIT } from '../../constants';

const ThickCloseIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path
            d="M18.5 6.91L17.09 5.5 12 10.59 6.91 5.5 5.5 6.91 10.59 12 5.5 17.09 6.91 18.5 12 13.41 17.09 18.5 18.5 17.09 13.41 12z"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </SvgIcon>
);

const TimeLimitRow = ({ classes, onRemove, onChange, value, isLastRow }) => {
    const { t } = useTranslation();
    const [unit, setUnit] = useState(value.unit || 'Minutes');

    const handleChange = useCallback(
        (field, newValue) => {
            const updatedValue = { ...value };

            if (field === 'maxBuzzes') {
                updatedValue.maxBuzzes = newValue;
            } else if (field === 'interval') {
                updatedValue.interval = newValue;
                updatedValue.timePeriodSeconds = calculateTimePeriodSeconds(
                    newValue,
                    unit,
                );
            } else if (field === 'unit') {
                setUnit(newValue);
                updatedValue.unit = newValue;
                updatedValue.timePeriodSeconds = calculateTimePeriodSeconds(
                    value.interval,
                    newValue,
                );
            }

            onChange(updatedValue);
        },
        [value, onChange, unit],
    );

    const calculateTimePeriodSeconds = (interval, unit) => {
        switch (unit) {
            case 'Minutes':
                return interval * 60;
            case 'Hours':
                return interval * 3600;
            case 'Days':
                return interval * 86400;
            default:
                return interval * 60;
        }
    };

    return (
        <div className={classes.timeLimitRow}>
            <div className={classes.numberInputWrapper}>
                <NumberInput
                    field="maxBuzzes"
                    value={value.maxBuzzes}
                    onChange={handleChange}
                    min={1}
                    max={100}
                />
            </div>
            <Typography variant="body1">
                <strong>{t('actions.create.every')}</strong>
            </Typography>
            <div className={classes.numberInputWrapper}>
                <NumberInput
                    field="interval"
                    value={value.interval}
                    onChange={handleChange}
                    min={1}
                    max={100}
                />
            </div>
            <div className={classes.unitSelectWrapper}>
                <select
                    value={unit}
                    onChange={(e) => handleChange('unit', e.target.value)}
                    className={classes.unitSelect}
                >
                    <option value="Minutes">
                        {t('actions.create.minutes')}
                    </option>
                    <option value="Hours">{t('actions.create.hours')}</option>
                    <option value="Days">{t('actions.create.days')}</option>
                </select>
                <ArrowDropDownIcon className={classes.selectArrow} />
            </div>
            <Tooltip
                title={isLastRow ? t('actions.create.lastRowTooltip') : ''}
            >
                <span>
                    <IconButton
                        onClick={onRemove}
                        className={`${classes.removeButton} ${
                            isLastRow ? classes.removeButtonDisabled : ''
                        }`}
                        disabled={isLastRow}
                    >
                        <ThickCloseIcon />
                    </IconButton>
                </span>
            </Tooltip>
        </div>
    );
};

const MAX_TIME_LIMITS = 3;

const TimeLimits = ({ classes, onChange, value }) => {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (value.length === 0) {
            onChange([DEFAULT_TIME_LIMIT]);
        }
    }, [value.length, onChange]);

    const addTimeLimit = useCallback(() => {
        if (value.length < MAX_TIME_LIMITS) {
            onChange([...value, DEFAULT_TIME_LIMIT]);
        }
    }, [value, onChange]);

    const removeTimeLimit = useCallback(
        (index) => {
            onChange(value.filter((_, i) => i !== index));
        },
        [value, onChange],
    );

    const updateTimeLimit = useCallback(
        (index, newValue) => {
            const newTimeLimits = [...value];
            newTimeLimits[index] = newValue;
            onChange(newTimeLimits);
        },
        [value, onChange],
    );

    const isMaxLimitsReached = value.length >= MAX_TIME_LIMITS;

    return (
        <div className={classes.timeLimitsContainer}>
            <Typography variant="subtitle1" className={classes.timeLimitsTitle}>
                {t('actions.create.timeLimits')}*
            </Typography>
            {value.map((timeLimit, index) => (
                <TimeLimitRow
                    key={index}
                    classes={classes}
                    onRemove={() => removeTimeLimit(index)}
                    onChange={(newValue) => updateTimeLimit(index, newValue)}
                    value={timeLimit}
                    isLastRow={value.length === 1}
                />
            ))}
            <Tooltip
                title={
                    isMaxLimitsReached
                        ? t('actions.create.maxTimeLimitsReached')
                        : ''
                }
                placement="top"
            >
                <span>
                    <Button
                        fullWidth
                        startIcon={
                            <AddIcon
                                className={classes.addIcon}
                                style={{
                                    color:
                                        isHovered && !isMaxLimitsReached
                                            ? 'white'
                                            : '',
                                }}
                            />
                        }
                        onClick={addTimeLimit}
                        className={`${classes.addButton} ${
                            isMaxLimitsReached ? classes.addButtonDisabled : ''
                        }`}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        disabled={isMaxLimitsReached}
                    >
                        {t('actions.create.addTimeLimit')}
                    </Button>
                </span>
            </Tooltip>
        </div>
    );
};

export default withStyles(styles)(TimeLimits);
